import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, Send, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { api } from '../api';
import { trackDemoLead } from '../lib/analytics';

// ---------------------------------------------------------------------------
// DemoLeadSection
// ---------------------------------------------------------------------------
// Full-width lead-capture section designed to sit at the bottom of every
// state / region landing page (Texas, Canada, United Kingdom, Worldwide,
// etc.). Trainers submit their info and Greg receives a Resend email alert.
//
// Props:
//   regionName        Human-readable label used in the heading + prefilled
//                     into the "Location" field. Examples: "Texas",
//                     "Canada", "the United Kingdom", "Worldwide".
//   regionSlug        Optional slug (e.g. "texas") appended to the notes so
//                     Greg can trace which landing page produced the lead.
//   eyebrow           Small kicker text above the heading. Defaults to
//                     "Schedule a demo".
//   headingOverride   Optional override for the H2 (defaults to a friendly
//                     region-aware line).
// ---------------------------------------------------------------------------
export const DemoLeadSection = ({
  regionName,
  regionSlug,
  eyebrow = 'Schedule a demo',
  headingOverride,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    facility_name: '',
    // Prefill the region name into the location field so trainers know we
    // already know where they are — they can still edit it (e.g. add city).
    location: regionName || '',
    website: '',
    notes: '',
  });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error('Please share your name and email so Greg can reach you.');
      return;
    }
    setSubmitting(true);
    try {
      // Tag the notes with a source marker so Greg can identify the
      // landing page that produced each demo request.
      const source = regionSlug
        ? `[Lead from /dog-training-software/${regionSlug}]`
        : `[Lead from ${regionName || 'landing page'}]`;
      const notes = form.notes ? `${source}\n\n${form.notes}` : source;
      await api.post('/demo-requests', { ...form, notes });
      // Fire GA4 conversion so demo requests show up in Reports → Realtime
      // and Reports → Engagement → Events as the primary lead event.
      trackDemoLead({
        source: regionSlug || 'unknown',
        region: regionName || 'unspecified',
      });
      toast.success("You're on the list — Greg will reach out shortly.");
      setForm({
        full_name: '',
        email: '',
        phone: '',
        facility_name: '',
        location: regionName || '',
        website: '',
        notes: '',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const heading =
    headingOverride ||
    (regionName
      ? `Ready to grow your ${regionName} dog-training business?`
      : 'Ready to see K9os in action?');

  return (
    <section
      className='relative py-16 sm:py-20 border-t border-slate-200 dark:border-slate-800 bg-gradient-to-br from-[#0B1220] via-[#0F1A33] to-[#1B3A87] text-white overflow-hidden'
      data-testid='demo-lead-section'
      data-region={regionSlug || regionName || 'generic'}
    >
      {/* Decorative glow */}
      <div
        aria-hidden='true'
        className='pointer-events-none absolute -top-32 -left-20 w-96 h-96 rounded-full bg-[#C8102E]/20 blur-3xl'
      />
      <div
        aria-hidden='true'
        className='pointer-events-none absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-[#93C5FD]/15 blur-3xl'
      />

      <div className='relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='max-w-2xl mx-auto text-center'>
          <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#F87171] mb-3'>
            <Sparkles className='w-3.5 h-3.5' /> {eyebrow}
          </div>
          <h2
            className='font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white leading-tight'
            data-testid='demo-lead-heading'
          >
            {heading}
          </h2>
          <p className='mt-4 text-slate-300 text-base sm:text-lg leading-relaxed'>
            Book a free 20-minute walkthrough with Greg. See how K9os &mdash; the all-in-one dog-trainer OS &mdash;
            can replace your current stack of scheduling, POS, memberships, contracts, and marketing tools with
            one login.
          </p>
        </div>

        <Card
          className='mt-10 p-6 sm:p-8 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-white/20 dark:border-slate-700 text-slate-900 dark:text-slate-100 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)]'
          data-testid='demo-lead-card'
        >
          <form onSubmit={onSubmit} className='space-y-4' data-testid='demo-lead-form'>
            <div>
              <Label htmlFor='dls-name'>Your full name *</Label>
              <Input
                id='dls-name'
                value={form.full_name}
                onChange={onChange('full_name')}
                placeholder='Jane Trainer'
                required
                data-testid='demo-lead-input-name'
              />
            </div>
            <div className='grid sm:grid-cols-2 gap-3'>
              <div>
                <Label htmlFor='dls-email'>Email *</Label>
                <Input
                  id='dls-email'
                  type='email'
                  value={form.email}
                  onChange={onChange('email')}
                  placeholder='you@yourfacility.com'
                  required
                  data-testid='demo-lead-input-email'
                />
              </div>
              <div>
                <Label htmlFor='dls-phone'>Phone (optional)</Label>
                <Input
                  id='dls-phone'
                  type='tel'
                  value={form.phone}
                  onChange={onChange('phone')}
                  placeholder='(555) 555-0123'
                  data-testid='demo-lead-input-phone'
                />
              </div>
            </div>
            <div>
              <Label htmlFor='dls-facility'>Facility / Business (optional)</Label>
              <Input
                id='dls-facility'
                value={form.facility_name}
                onChange={onChange('facility_name')}
                placeholder='Your K9 training business'
                data-testid='demo-lead-input-facility'
              />
            </div>
            <div className='grid sm:grid-cols-2 gap-3'>
              <div>
                <Label htmlFor='dls-location'>City &amp; region (optional)</Label>
                <Input
                  id='dls-location'
                  value={form.location}
                  onChange={onChange('location')}
                  placeholder={regionName ? `e.g. Austin, ${regionName}` : 'City, region'}
                  data-testid='demo-lead-input-location'
                />
              </div>
              <div>
                <Label htmlFor='dls-website'>Website or social (optional)</Label>
                <Input
                  id='dls-website'
                  type='url'
                  inputMode='url'
                  value={form.website}
                  onChange={onChange('website')}
                  placeholder='https://yourfacility.com or @handle'
                  data-testid='demo-lead-input-website'
                />
              </div>
            </div>
            <div>
              <Label htmlFor='dls-notes'>What would you like to see? (optional)</Label>
              <Textarea
                id='dls-notes'
                rows={3}
                value={form.notes}
                onChange={onChange('notes')}
                placeholder='Bookings, memberships, POS, marketing, contracts…'
                data-testid='demo-lead-input-notes'
              />
            </div>
            <Button
              type='submit'
              disabled={submitting}
              className='k9-demo-cta w-full bg-[#C8102E] hover:bg-[#A50D26] text-white h-14 text-base sm:text-lg font-bold rounded-xl'
              data-testid='demo-lead-submit-btn'
            >
              {submitting ? (
                <>
                  <Loader2 className='w-5 h-5 mr-2 animate-spin' /> Sending&hellip;
                </>
              ) : (
                <>
                  <Send className='w-5 h-5 mr-2' /> Request a Free Demo
                </>
              )}
            </Button>
            <p className='text-[11px] text-slate-500 dark:text-slate-400 text-center'>
              By submitting you agree we may email you about your demo request. No spam &mdash; ever.
            </p>
          </form>
        </Card>

        {/* Quick alt-CTA row for trainers who prefer email/chat instead of the form */}
        <div className='mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-300'>
          <a
            href='mailto:GregMiceli2018@gmail.com'
            className='inline-flex items-center gap-1.5 hover:text-white transition-colors'
            data-testid='demo-lead-email-fallback'
          >
            <MessageCircle className='w-4 h-4' /> Or email Greg directly
          </a>
          <span aria-hidden='true' className='text-slate-500'>&middot;</span>
          <span className='text-slate-400'>Replies within one business day</span>
        </div>
      </div>
    </section>
  );
};

export default DemoLeadSection;
